import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TeamCreateModal from '../../components/commonComponent/TeamCreateModal';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const ContributionTeamPage = () => {
  const navigate = useNavigate();
  const [contributionTeam, setContributionTeam] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({
    contributionLevel: '',
    chapter: '',
    coreTeamMaster: '',
    team: '',
    head: [],
    partner: [],
    appMenu: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dropdowns, setDropdowns] = useState({ 
    contributionLevels: [], 
    chapters: [], 
    coreTeamMasters: [], 
    teams: [], 
    members: [], 
    appMenus: [] 
  });

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);
  // Fetch dropdowns for contribution levels, chapters, core team masters, teams, members, app menus
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [
          contributionLevelRes,
          chapterRes,
          teamRes,
          coreTeamRes,
          membersRes
        ] = await Promise.all([
          axios.get('/admin/api/contribution-levels'),
          axios.get('/admin/api/chapters/all'),
          axios.get('/admin/api/team'),
          axios.get('/admin/api/core-team/core'),
          axios.get('/api/user')
        ]);
        setDropdowns({
          contributionLevels: (contributionLevelRes.data?.contributionLevels || []).map(cl => ({ value: cl._id, label: cl.name })),
          chapters: (chapterRes.data?.chapter || []).map(ch => ({ value: ch._id, label: ch.name })),
          teams: (teamRes.data?.teams || []).map(t => ({ value: t._id, label: t.name })),
          coreTeamMasters: (coreTeamRes.data?.coreTeams || []).map(ct => ({
            value: ct.coreTeamMaster?._id || ct._id,
            label: `${ct.coreTeamMaster?.name || ct.name || ''} (${ct.contributionLevel?.name || ''})`
          })),
          members: (membersRes.data?.users || []).map(m => ({ value: m._id, label: `${m.firstName} ${m.lastName}` }))
        });
      } catch (err) {
        toastError('Failed to fetch dropdowns');
      }
    };
    fetchDropdowns();
  }, []);

  // Fetch contribution levels separately to ensure parent has the data
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const contributionLevelRes = await axios.get('/admin/api/contribution-levels');
        setDropdowns(prev => ({
          ...prev,
          contributionLevels: (contributionLevelRes.data?.contributionLevels || []).map(cl => ({
            value: cl._id,
            label: cl.name
          }))
        }));
      } catch (err) {
        toastError('Failed to fetch contribution levels');
      }
    };
    fetchDropdowns();
  }, []);

  // Filter chapters based on selected cluster
  const filteredChapters = dropdowns.chapters;

  // Debounce search query - only trigger when 3+ characters or empty
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchContributionTeam(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery]);

  const fetchContributionTeam = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`/admin/api/contribution-team?page=${page}&limit=${limit}&search=${search}`);
      const { contributionTeams = [], totalCount = 0 } = res.data;

      setContributionTeam(contributionTeams);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Contribution Team:', err);
    }
  };

  const handleSave = async () => {
    const validationRules = {
      contributionLevel: { required: true },
      coreTeamMaster: { required: true },
      team: { required: true },
      head: { required: true },
      partner: { required: true },
    };

    // Chapter is optional - no validation required

    const validationErrors = validateForm(formValues, validationRules);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      // Prepare data for API - convert arrays to comma-separated strings
      const apiData = {
        contributionLevel: formValues.contributionLevel,
        coreTeamMaster: formValues.coreTeamMaster,
        team: formValues.team,
        head: Array.isArray(formValues.head) ? formValues.head.join(',') : formValues.head,
        partner: Array.isArray(formValues.partner) ? formValues.partner.join(',') : formValues.partner,
      };

      // Include chapter if it has a value
      if (formValues.chapter) {
        apiData.chapter = formValues.chapter;
      }

      if (editingId) {
        await axios.put(`/admin/api/contribution-team/${editingId}`, apiData);
        toastSuccess('Contribution Team updated successfully!');
      } else {
        await axios.post('/admin/api/contribution-team', apiData);
        toastSuccess('Contribution Team added successfully!');
      }
      setCurrentPage(1);
      fetchContributionTeam(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Contribution Team:', err);
      toastError('Failed to save Contribution Team');
    }
  };

  const handleEdit = (contributionTeam) => {
    setFormValues({
      contributionLevel: contributionTeam.contributionLevel?._id || contributionTeam.contributionLevel || '',
      chapter: contributionTeam.chapter?._id || contributionTeam.chapter || '',
      coreTeamMaster: contributionTeam.coreTeamMaster?._id || contributionTeam.coreTeamMaster || '',
      team: contributionTeam.team?._id || contributionTeam.team || '',
      head: Array.isArray(contributionTeam.head) ? contributionTeam.head.map(h => h._id || h) : [],
      partner: Array.isArray(contributionTeam.partner) ? contributionTeam.partner.map(p => p._id || p) : [],
      appMenu: contributionTeam.appMenu || '',
    });
    setEditingId(contributionTeam._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure you want to delete this Contribution Team?');
    if (result?.isConfirmed) {
      try {
        await axios.delete(`/admin/api/contribution-team/${id}`);
        toastSuccess('Contribution Team deleted successfully!');
        setCurrentPage(1);
        fetchContributionTeam(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Contribution Team:', err);
        toastError('Failed to delete Contribution Team');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({
      contributionLevel: '',
      chapter: '',
      coreTeamMaster: '',
      team: '',
      head: [],
      partner: [],
      appMenu: '',
    });
    setEditingId(null);
    setErrors({});
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Contribution Team</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Contribution Team (min 3 chars)..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Contribution Team List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add Contribution Team
          </button>
        </div>
       <div className="table-responsive">
        <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Contribution Level</th>
                <th className="py-1 px-2">Chapter</th>
                <th className="py-1 px-2">Core Team Master</th>
                <th className="py-1 px-2">Team</th>
                <th className="py-1 px-2">Head</th>
                <th className="py-1 px-2">Partners</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contributionTeam.length > 0 ? (
                contributionTeam.map((member, idx) => {
                  const contributionLevelLabel = member.contributionLevel?.name || 
                    dropdowns.contributionLevels.find(cl => cl.value === member.contributionLevel)?.label || '';
                  const chapterLabel = member.chapter?.name || 
                    dropdowns.chapters.find(c => c.value === member.chapter)?.label || '-';
                  const coreTeamMasterLabel = member.coreTeamMaster?.name || 
                    dropdowns.coreTeamMasters.find(ct => ct.value === member.coreTeamMaster)?.label || '';
                  const teamLabel = member.team?.name || 
                    dropdowns.teams.find(t => t.value === member.team)?.label || '';
                  
                  // Handle head (can be array of objects or IDs)
                  let headLabels = '';
                  if (Array.isArray(member.head) && member.head.length > 0) {
                    headLabels = member.head.map(head => {
                      if (typeof head === 'object' && head.firstName) {
                        return `${head.firstName} ${head.lastName}`;
                      }
                      return dropdowns.members.find(m => m.value === head)?.label || '';
                    }).filter(label => label).join(', ');
                  }
                  
                  // Handle partner (can be array of objects or IDs)
                  let partnerLabels = '';
                  if (Array.isArray(member.partner) && member.partner.length > 0) {
                    partnerLabels = member.partner.map(partner => {
                      if (typeof partner === 'object' && partner.firstName) {
                        return `${partner.firstName} ${partner.lastName}`;
                      }
                      return dropdowns.members.find(m => m.value === partner)?.label || '';
                    }).filter(label => label).join(',');
                  }
                  
                  return (
                    <tr key={member._id}>
                      <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                      <td className="py-1 px-2">{contributionLevelLabel}</td>
                      <td className="py-1 px-2">{chapterLabel}</td>
                      <td className="py-1 px-2">{coreTeamMasterLabel}</td>
                      <td className="py-1 px-2">{teamLabel}</td>
                      <td className="py-1 px-2">{headLabels}</td>
                      <td className="py-1 px-2">{partnerLabels}</td>
                      <td className="py-1 px-2">
                        <button
                          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                          title="Edit"
                          onClick={() => handleEdit(member)}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                          onClick={() => handleDelete(member._id)}
                          title="Delete"
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-2">No Contribution Team found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

             {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
               <button
                  key={page}
                  className="{`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      <TeamCreateModal
        visible={showModal}
        title={editingId ? 'Edit Contribution Team' : 'Team Create'}
        values={formValues}
        errors={errors}
        dropdowns={dropdowns}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        disabled={false}
      />
    </div>
  );
};

export default ContributionTeamPage;
